import {api, routes} from "@/plugins/axios";

export function useMembers() {
    const getMember = (organizationSlug, memberUid, withRelation) => {
        return api
            .get(routes.members.get(organizationSlug, memberUid, withRelation))
            .then((response) => {
                return Promise.resolve(response);
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    };

    const getMemberByUserUid = (organizationSlug, userUid) => {
        return api
            .get(routes.members.showByUser(organizationSlug, userUid))
            .then((response) => {
                return Promise.resolve(response);
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    };

    const getMemberGroups = (organizationSlug, memberUid) => {
        return api
            .get(routes.members.getMemberGroups(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response);
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    }

    const updateMember = (organizationSlug, memberUid, memberData) => {
        const data = {
            first_name: memberData.firstName,
            last_name: memberData.lastName,
            middle_name: memberData.middleName,
            status: memberData.status,
        }

        if (memberData.roles) {
            data.roles = memberData.roles;
        }

        return api
            .put(routes.members.manage.update(organizationSlug, memberUid), data)
            .catch((err) => {
                return Promise.reject(err);
            });
    };

    const uploadAvatar = (organizationSlug, memberUid, memberPhoto) => {
        const formData = new FormData();
        formData.append("avatar", memberPhoto);

        return api
            .post(routes.members.avatar.store(organizationSlug, memberUid), formData)
            .catch((err) => {
                return Promise.reject(err);
            });
    };

    const deleteAvatar = (organizationSlug, memberUid) => {
        return api
            .delete(routes.members.avatar.delete(organizationSlug, memberUid))
            .catch((err) => {
                return Promise.reject(err);
            });
    };

    const sendPhoneCode = (organizationSlug, memberUid, phoneNumber) => {
        return api
            .post(routes.members.phoneNumbers.sendCode(organizationSlug, memberUid), {
                phone_number: phoneNumber,
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const checkPhoneCode = (organizationSlug, memberUid, code, phoneNumber) => {
        return api
            .post(
                routes.members.phoneNumbers.checkCode(organizationSlug, memberUid),
                {
                    code: code,
                    phone_number: phoneNumber,
                }
            )
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const sendEmailCode = (organizationSlug, memberUid, email) => {
        return api
            .post(routes.members.emails.sendCode(organizationSlug, memberUid), {
                email: email,
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const checkEmailCode = (organizationSlug, memberUid, code, email) => {
        return api
            .post(routes.members.emails.checkCode(organizationSlug, memberUid), {
                code: code,
                email: email,
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const createPhoneNumber = (organizationSlug, memberUid, phoneNumber) => {
        return api
            .post(routes.members.phoneNumbers.create(organizationSlug, memberUid), {
                phone_number: phoneNumber,
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const createEmail = (organizationSlug, memberUid, email) => {
        return api
            .post(routes.members.emails.create(organizationSlug, memberUid), {
                email: email,
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const getPrimaryPhoneNumber = (phone_numbers) => {
        return phone_numbers.find((phone) => phone.is_primary) || null;
    };

    const getPrimaryEmailAddress = (emails) => {
        return emails.find((email) => email.is_primary) || null;
    };

    const getMembers = ({
        organizationSlug,
        page = 1,
        perPage = 10,
        search = "",
        groups = [],
        accountStatuses = [],
        orderBy = "name",
        orderByDirection = "asc",
        fields = [],
        filtersOperator = "and",
        includeFields = false,
        leadershipOnly = false,
        excludeUids = [],
        uids = [],
        excludeGroups = [],
        signal,
    }) => {
        return api
            .get(
                routes.members.list(
                    organizationSlug,
                    page,
                    perPage,
                    search,
                    groups,
                    accountStatuses,
                    orderBy,
                    orderByDirection,
                    fields,
                    filtersOperator,
                    includeFields,
                    leadershipOnly,
                    excludeUids,
                    uids,
                    excludeGroups,
                ),
                { signal }
            )
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    };

    const downloadMembersList = async (
        organizationSlug,
        search = "",
        groups = [],
        statuses = [],
        orderBy = "name",
        direction = "asc",
        fields = [],
        filtersOperator = "and",
        includeBeneficiaries = false,
        selectedMembers = { include: [], exclude: [] },
    ) => {
        try {
            const response = await api.get(
                routes.members.download(
                    organizationSlug,
                    search,
                    groups,
                    statuses,
                    orderBy,
                    direction,
                    fields,
                    filtersOperator,
                    includeBeneficiaries,
                    selectedMembers,
                ),
                {responseType: "blob"}
            );

            // Check if response is JSON (queued) by checking content-type
            const contentType = response.headers['content-type'];

            if (contentType && contentType.includes('application/json')) {
                // Response is JSON, convert blob to JSON
                const text = await response.data.text();
                const jsonData = JSON.parse(text);

                if (jsonData.queued) {
                    return Promise.resolve({
                        queued: true,
                        batch_id: jsonData.batch_id,
                        message: jsonData.message,
                        count: jsonData.count
                    });
                }
            }

            // Response is a blob (file), trigger download
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            const currentDate = new Date().toLocaleDateString("en-US");
            const filename = `members_export_${organizationSlug}-${currentDate}.xlsx`;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return Promise.resolve({ queued: false });
        } catch (err) {
            return Promise.reject(err);
        }
    };

    const downloadPedigreeSheet = (organizationSlug, memberUID) =>
        api.get(routes.members.pedigreeSheet(organizationSlug, memberUID), { responseType: "blob" });

    const createLeader = (organizationSlug, memberUid, workTitle) => {
        return api
            .post(routes.members.createLeader(organizationSlug, memberUid), {
                work_title: workTitle,
            })
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const getAvailableStatuses = (organizationSlug) => {
        return api
            .get(routes.members.getAvailableStatuses(organizationSlug))
            .then((response) => {
                return Promise.resolve(response);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const activateMember = (organizationSlug, memberUid) => {
        return api
            .post(routes.members.manage.activate(organizationSlug), {
                uid: memberUid,
            })
            .then((response) => {
                return Promise.resolve(response);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const deactivateMember = (organizationSlug, memberUid) => {
        return api
            .delete(routes.members.manage.deactivate(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const getRoles = (organizationSlug) => {
        return api
            .get(routes.organization.getRoles(organizationSlug))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const getProfileStatus = (organizationSlug, memberUid) => {
        return api
            .get(routes.members.getProfileStatus(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data.status);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const getFieldsStatus = (organizationSlug, memberUid) => {
        return api
            .get(routes.members.getFieldsStatus(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data.status);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const getOnboardingStatus = (organizationSlug, memberUid) => {
        return api
            .get(routes.members.getOnboardingStatus(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data.status);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const finishOnboarding = async (organizationSlug, memberUid) => {
        return api
            .patch(routes.members.finishOnboarding(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const resendInvitation = async (organizationSlug, memberUid) => {
        return api
            .post(routes.members.manage.resendInvitation(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const convertToInvited = async (organizationSlug, memberUid) => {
        return api
            .post(routes.members.manage.convertToInvited(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const bulkConvertToInvited = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk.convertToInvited(organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkDelete = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk.delete(organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const getMemberAuthenticationNumber = (organizationSlug, memberUid) => {
        return api
            .get(routes.members.getAuthenticationNumber(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const getMemberHistory = (organizationSlug, memberUid, property, uid = null) => {
        return api
            .get(routes.members.history(organizationSlug, memberUid, property, uid))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkActivate = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk.activate(organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkUpdate = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk.update(organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkDeactivate = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk.deactivate(organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkResendInvitation = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk.resendInvitation(organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkInviteToGroup = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk['add-to-groups'](organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkRemoveFromGroups = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk['remove-from-groups'](organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const bulkConvertToNonactive = async (organizationSlug, payload) => {
        return api
            .post(routes.members.bulk.convertToNonactive(organizationSlug), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    }

    const convertToNonactive = async (organizationSlug, memberUid) => {
        return api
            .post(routes.members.manage.convertToNonactive(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const setMilitaryLeave = async (organizationSlug, memberUid, payload) => {
        return api
            .patch(routes.members.manage.militaryLeave(organizationSlug, memberUid), payload)
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const clearMilitaryLeave = async (organizationSlug, memberUid) => {
        return api
            .delete(routes.members.manage.militaryLeave(organizationSlug, memberUid))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const mergeMembers = (organizationSlug, targetUid, sourceUid) => {
        return api
            .post(routes.members.manage.merge(organizationSlug, targetUid), {
                source_uid: sourceUid,
            })
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((error) => {
                return Promise.reject(error);
            });
    };

    const getMembersInGroup = (organizationSlug, groupsName, statuses, page, perPage) => {
        return api
            .get(routes.members.getMembersInGroup(organizationSlug, groupsName, statuses, page, perPage))
            .then((response) => {
                return Promise.resolve(response.data);
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    }

    return {
        getMember,
        getMemberByUserUid,
        updateMember,
        getMembers,
        sendEmailCode,
        checkEmailCode,
        sendPhoneCode,
        checkPhoneCode,
        createPhoneNumber,
        createEmail,
        getPrimaryEmailAddress,
        getPrimaryPhoneNumber,
        uploadAvatar,
        deleteAvatar,
        downloadMembersList,
        downloadPedigreeSheet,
        createLeader,
        getAvailableStatuses,
        activateMember,
        deactivateMember,
        getRoles,
        getProfileStatus,
        getFieldsStatus,
        getOnboardingStatus,
        finishOnboarding,
        resendInvitation,
        bulkDelete,
        getMemberAuthenticationNumber,
        getMemberHistory,
        bulkActivate,
        bulkUpdate,
        bulkDeactivate,
        bulkResendInvitation,
        bulkInviteToGroup,
        bulkRemoveFromGroups,
        convertToInvited,
        bulkConvertToInvited,
        bulkConvertToNonactive,
        convertToNonactive,
        setMilitaryLeave,
        clearMilitaryLeave,
        mergeMembers,
        getMembersInGroup,
        getMemberGroups
    };
}
